/**
 * Service: whatsappService.js
 * 
 * Integração com WhatsApp via links wa.me.
 * Nenhum backend necessário — o dispositivo do usuário abre o WhatsApp
 * com a mensagem já preenchida. O usuário só precisa clicar em Enviar.
 */

// ─── Utilitários ──────────────────────────────────────────────────────────────

/**
 * Converte um número de telefone para o formato internacional brasileiro
 * usado pelo wa.me (ex: 5511987654321).
 * Suporta: (11) 98765-4321 | 011987654321 | +5511987654321 | 11987654321
 */
export const formatPhoneForWhatsApp = (phone) => {
  if (!phone) return null;
  let digits = phone.replace(/\D/g, '');
  // Já tem código de país completo
  if (digits.startsWith('55') && digits.length >= 12) return digits;
  // Remove 0 inicial (prefixo antigo brasileiro)
  if (digits.startsWith('0')) digits = digits.slice(1);
  // Adiciona código do Brasil
  return '55' + digits;
};

/**
 * Codifica uma string em percent-encoding UTF-8, byte a byte.
 * Usa TextEncoder para garantir UTF-8 correto para TODOS os caracteres,
 * incluindo emojis acima de U+FFFF que encodeURIComponent pode corromper.
 */
const percentEncodeUTF8 = (text) => {
  const utf8 = new TextEncoder().encode(text);
  const parts = [];
  for (let i = 0; i < utf8.length; i++) {
    const b = utf8[i];
    // Caracteres ASCII seguros (RFC 3986 unreserved) ficam legíveis
    if ((b >= 65 && b <= 90) || (b >= 97 && b <= 122) ||
        (b >= 48 && b <= 57) || b === 45 || b === 46 || b === 95 || b === 126) {
      parts.push(String.fromCharCode(b));
    } else {
      parts.push('%' + b.toString(16).toUpperCase().padStart(2, '0'));
    }
  }
  return parts.join('');
};

/**
 * Monta a URL completa do wa.me com mensagem codificada.
 */
export const buildWhatsAppUrl = (phone, text) => {
  const formatted = formatPhoneForWhatsApp(phone);
  if (!formatted) return null;
  return `https://wa.me/${formatted}?text=${percentEncodeUTF8(text)}`;
};

/**
 * Abre o WhatsApp em nova aba.
 * @returns {boolean} true se o link foi aberto, false se o telefone estava vazio.
 */
export const openWhatsApp = (phone, text) => {
  const url = buildWhatsAppUrl(phone, text);
  if (!url) return false;
  window.open(url, '_blank', 'noopener,noreferrer');
  return true;
};

/**
 * Formata uma data ISO (YYYY-MM-DD) para o padrão pt-BR (DD/MM/YYYY).
 */
export const formatDateBR = (dateStr) => {
  if (!dateStr) return dateStr;
  return new Date(dateStr + 'T12:00:00').toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

/**
 * Mapeia o campo paymentMethod para texto legível.
 */
export const formatPaymentMethod = (method, cardType) => {
  if (method === 'cash') return 'Dinheiro';
  if (method === 'pix') return 'Pix';
  if (method === 'card') return cardType === 'credit' ? 'Cartão de Crédito' : 'Cartão de Débito';
  return method || 'Não informado';
};

// ─── Emojis (String.fromCodePoint garante renderização correta em qualquer ambiente) ──
const em = {
  wave:      String.fromCodePoint(0x1F44B),
  calendar:  String.fromCodePoint(0x1F4C5),
  clock:     String.fromCodePoint(0x23F0),
  scissors:  String.fromCodePoint(0x2702) + String.fromCodePoint(0xFE0F),
  check:     String.fromCodePoint(0x2705),
  barber:    String.fromCodePoint(0x1F488),
  money:     String.fromCodePoint(0x1F4B0),
  siren:     String.fromCodePoint(0x1F6A8),
  warning:   String.fromCodePoint(0x26A0) + String.fromCodePoint(0xFE0F),
  sparkles:  String.fromCodePoint(0x2728),
};

// ─── Templates de Mensagens ───────────────────────────────────────────────────

/**
 * CLIENTE → BARBEARIA
 * Enviada pelo cliente após solicitar um agendamento.
 */
export const msgClientRequestsBooking = ({ barbershopName, service, date, time }) =>
  `Olá, ${barbershopName}! ${em.wave}\n\nAcabei de solicitar um agendamento pelo Barberize.\n\n${em.calendar} Data: ${formatDateBR(date)}\n${em.clock} Horário: ${time}\n${em.scissors} Serviço: ${service}\n\nFico no aguardo da confirmação. Obrigado!`;

/**
 * CLIENTE → BARBEARIA
 * Enviada pelo cliente ao entrar na fila de espera.
 */
export const msgClientEntersWaitlist = ({ barbershopName, service, desiredDate, desiredTime }) =>
  `Olá, ${barbershopName}! ${em.wave}\n\nEntrei na fila de espera pelo Barberize para o serviço abaixo:\n\n${em.scissors} Serviço: ${service}\n${em.calendar} Data desejada: ${formatDateBR(desiredDate)}\n${em.clock} Horário desejado: ${desiredTime}\n\nFico no aguardo caso algum horário seja liberado. Obrigado!`;

/**
 * CLIENTE → BARBEARIA
 * Enviada pelo cliente ao aceitar um slot liberado pela barbearia.
 */
export const msgClientAcceptsReleasedSlot = ({ barbershopName, service, date, time }) =>
  `Olá, ${barbershopName}! ${em.wave}\n\nAcabei de confirmar o horário liberado pelo Barberize.\n\n${em.calendar} Data: ${formatDateBR(date)}\n${em.clock} Horário: ${time}\n${em.scissors} Serviço: ${service}\n\nObrigado! Nos vemos em breve.`;

/**
 * BARBEARIA → CLIENTE
 * Enviada pelo dono ao confirmar um agendamento pendente.
 */
export const msgBarbershopConfirmsBooking = ({ clientName, service, date, time }) =>
  `Olá, ${clientName}! ${em.wave}\n\nSeu agendamento foi confirmado com sucesso pelo Barberize ${em.check}\n\n${em.calendar} Data: ${formatDateBR(date)}\n${em.clock} Horário: ${time}\n${em.scissors} Serviço: ${service}\n\nQualquer dúvida ou necessidade de alteração, é só entrar em contato.\nTe esperamos!`;

/**
 * BARBEARIA → CLIENTE
 * Enviada pelo dono ao concluir o atendimento.
 */
export const msgServiceCompleted = ({ clientName, service, paymentMethod, cardType }) =>
  `Olá, ${clientName}! ${em.scissors}${em.sparkles}\n\nSeu atendimento foi concluído com sucesso. Esperamos que tenha gostado!\n\n${em.barber} Serviço: ${service}\n${em.money} Pagamento: ${formatPaymentMethod(paymentMethod, cardType)}\n\nSua avaliação já está disponível no Barberize.\nAcesse a aba *Avaliações* e conte pra gente como foi sua experiência — sua opinião é muito importante!\n\nAté a próxima!`;

/**
 * BARBEARIA → CLIENTE
 * Enviada pelo dono ao registrar não comparecimento.
 */
export const msgClientNoShow = ({ clientName, date, time }) =>
  `Olá, ${clientName}.\n\nNotamos que o agendamento de hoje (${formatDateBR(date)} às ${time}) não teve comparecimento.\n\nCaso tenha ocorrido algum imprevisto, fique à vontade para reagendar pelo Barberize.\nEstamos à disposição.`;

/**
 * BARBEARIA → CLIENTE
 * Enviada pelo dono ao liberar um horário para o cliente na fila de espera.
 */
export const msgBarbershopReleasesSlot = ({ clientName, service, date, time, reservationMinutes = 30 }) =>
  `Olá, ${clientName}! ${em.siren}${em.scissors}\n\nUm horário acabou de ser liberado para você pelo Barberize!\n\n${em.calendar} Data: ${formatDateBR(date)}\n${em.clock} Horário: ${time}\n${em.scissors} Serviço: ${service}\n\nAcesse o Barberize para confirmar o agendamento.\n${em.warning} Esse horário ficará reservado para você por ${reservationMinutes} minutos.`;

/**
 * BARBEARIA → CLIENTE
 * Enviada quando o slot liberado expira sem confirmação.
 */
export const msgReleasedSlotExpired = ({ clientName, date, time }) =>
  `Olá, ${clientName}.\n\nO horário liberado para ${formatDateBR(date)} às ${time} não foi confirmado dentro do tempo disponível.\n\nVocê continua na fila de espera e será avisado caso um novo horário seja liberado pelo Barberize.`;
